import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  PlanDay,
  PlanFrame,
  ApprovalStatus,
  fetchPlan,
  createDay,
  createFrame,
  updateFrame,
  uploadPlannerImage,
  setReviewApproved,
  sendBackToDraft,
  setFinalApproved,
  setRejected,
  canEnterApproveStep,
  isReviewApproved,
  isFinalApproved,
} from '@/lib/contentPlanner';
import {
  Compass,
  Plus,
  Sparkles,
  ImagePlus,
  CheckCircle2,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Upload,
  Loader2,
  Cloud,
  BarChart3,
  Eye,
  ListChecks,
  Lock,
  XCircle,
  RotateCcw,
} from 'lucide-react';

/**
 * Zera Content Studio — the installable iOS/Android app.
 *
 * Mirrors the website backend's 5-step content workflow 1:1, reading/writing the
 * SAME Supabase tables (content_plan_days / content_plan_frames) and storage as
 * /mtnadmin/content-planner. Supabase is the single source of truth.
 *
 * TWO-STAGE APPROVAL GATE
 * -----------------------
 * Step 3 (Review)  must mark a post "Review Approved" before it can advance.
 * Step 4 (Approve) is LOCKED until Step 3 review approval exists. In Step 4 the
 * approver either grants final approval (→ Step 5 Schedule) or disapproves, which
 * sends the post BACK to Step 3 ("Rejected") for reapproval. Once reapproved, the
 * post becomes eligible for Step 4 again. Every transition is persisted to the
 * shared backend instantly.
 */

type Step = 0 | 1 | 2 | 3 | 4;

const STEPS: { label: string; short: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { label: 'Create', short: 'Create', icon: Plus },
  { label: 'Generate', short: 'Generate', icon: Sparkles },
  { label: 'Review', short: 'Review', icon: Eye },
  { label: 'Approve', short: 'Approve', icon: CheckCircle2 },
  { label: 'Schedule', short: 'Schedule', icon: CalendarClock },
];

const statusColor: Record<ApprovalStatus, string> = {
  Draft: 'bg-slate-100 text-slate-700',
  'Review Approved': 'bg-sky-100 text-sky-700',
  Rejected: 'bg-rose-100 text-rose-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Scheduled: 'bg-[#C9A961]/20 text-[#8B6F47]',
};

const MobileApp: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(0);
  const [days, setDays] = useState<PlanDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDayId, setActiveDayId] = useState<string | null>(null);
  const [activeFrameId, setActiveFrameId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [synced, setSynced] = useState(false);
  const [gateMsg, setGateMsg] = useState<string | null>(null);

  // create-step inputs
  const [newDayLabel, setNewDayLabel] = useState('');
  const [newTheme, setNewTheme] = useState('');

  const loadAll = async () => {
    setLoading(true);
    const data = await fetchPlan('feed');
    setDays(data);
    setLoading(false);
    return data;
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeDay = useMemo(() => days.find((d) => d.id === activeDayId) || null, [days, activeDayId]);
  const activeFrame: PlanFrame | null = useMemo(() => {
    if (!activeDay) return null;
    return activeDay.frames.find((f) => f.id === activeFrameId) || activeDay.frames[0] || null;
  }, [activeDay, activeFrameId]);

  const status: ApprovalStatus = activeFrame?.approval_status || 'Draft';
  const reviewApproved = isReviewApproved(status);
  const finalApproved = isFinalApproved(status);
  const rejected = status === 'Rejected';

  const flash = () => {
    setSynced(true);
    window.setTimeout(() => setSynced(false), 1800);
  };

  const showGate = (msg: string) => {
    setGateMsg(msg);
    window.setTimeout(() => setGateMsg(null), 2600);
  };

  // ---------- STEP 1: CREATE ----------
  const handleCreate = async () => {
    setBusy('create');
    const dayId = await createDay('feed', days.length);
    if (dayId) {
      await supabase
        .from('content_plan_days')
        .update({
          day_label: newDayLabel || 'New Post',
          theme: newTheme || '',
          date_label: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        })
        .eq('id', dayId);
      const frameId = await createFrame(dayId, 0, 'feed');
      const data = await loadAll();
      setActiveDayId(dayId);
      setActiveFrameId(frameId);
      setNewDayLabel('');
      setNewTheme('');
      flash();
      void data;
      setStep(1);
    }
    setBusy(null);
  };

  const selectExisting = (day: PlanDay) => {
    setActiveDayId(day.id);
    setActiveFrameId(day.frames[0]?.id || null);
    setStep(1);
  };

  // ---------- STEP 2: GENERATE ----------
  const generateCaption = async () => {
    if (!activeFrame || !activeDay) return;
    setBusy('caption');
    try {
      const { data, error } = await supabase.functions.invoke('caption-suggest', {
        body: {
          theme: activeDay.theme || activeDay.day_label,
          dayLabel: activeDay.day_label,
          postType: activeFrame.post_type,
          imageUrl: activeFrame.image || undefined,
          existingCaption: activeFrame.caption || undefined,
        },
      });

      if (error) throw new Error(error.message || 'Caption service error');
      if (data?.error) throw new Error(data.error);

      // The edge function returns:
      //   { captions: [{ style, caption }], hashtags: { broad, mid, niche } }
      const caption: string =
        data?.captions?.[0]?.caption ||
        data?.captions?.[0]?.text ||
        (typeof data?.captions?.[0] === 'string' ? data.captions[0] : '') ||
        data?.caption ||
        '';

      // hashtags arrive as a tiered object — flatten into a single array.
      const h = data?.hashtags;
      let hashtags: string[] = [];
      if (Array.isArray(h)) {
        hashtags = h;
      } else if (h && typeof h === 'object') {
        hashtags = [...(h.broad || []), ...(h.mid || []), ...(h.niche || [])];
      }
      hashtags = hashtags.filter(Boolean).slice(0, 30);

      if (caption) {
        await updateFrame(activeFrame.id, { caption, hashtags });
        flash();
        await loadAll();
      } else {
        showGate('AI returned no caption — please try again.');
      }
    } catch (e: any) {
      showGate(`Caption failed: ${e?.message || 'try again'}`);
    }
    setBusy(null);
  };

  const generateImage = async () => {
    if (!activeFrame || !activeDay) return;
    setBusy('image');
    try {
      // The edge function REQUIRES a `post` object (not a raw prompt).
      const { data, error } = await supabase.functions.invoke('generate-post-image', {
        body: {
          post: {
            post_type: activeFrame.post_type || 'feed',
            theme: activeDay.theme || activeDay.day_label,
            caption: activeFrame.caption || '',
            image_prompt: `${activeDay.theme || activeDay.day_label}. Luxury Albanian travel photography, editorial Instagram aesthetic, natural light, cinematic composition.`,
          },
        },
      });

      if (error) throw new Error(error.message || 'Image service error');
      if (data?.error) throw new Error(data.error);

      const url =
        data?.image_url || data?.imageUrl || data?.url || data?.image || data?.images?.[0] || '';
      if (url) {
        await updateFrame(activeFrame.id, { image: url });
        flash();
        await loadAll();
      } else {
        showGate('AI returned no image — please try again.');
      }
    } catch (e: any) {
      showGate(`Image failed: ${e?.message || 'try again'}`);
    }
    setBusy(null);
  };


  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeFrame) return;
    setBusy('upload');
    const url = await uploadPlannerImage(file);
    if (url) {
      await updateFrame(activeFrame.id, { image: url });
      flash();
      await loadAll();
    }
    setBusy(null);
    e.target.value = '';
  };

  const patchFrame = async (patch: Partial<PlanFrame>) => {
    if (!activeFrame) return;
    await updateFrame(activeFrame.id, patch);
    setDays((prev) =>
      prev.map((d) =>
        d.id === activeDayId
          ? { ...d, frames: d.frames.map((f) => (f.id === activeFrame.id ? { ...f, ...patch } : f)) }
          : d,
      ),
    );
    flash();
  };

  // ---------- STEP 3: REVIEW (Stage 1 approval gate) ----------
  const reviewApprove = async () => {
    if (!activeFrame || !activeDayId) return;
    setBusy('review');
    await setReviewApproved(activeFrame.id, activeDayId);
    await loadAll();
    flash();
    setBusy(null);
  };

  const reviewSendBack = async () => {
    if (!activeFrame || !activeDayId) return;
    setBusy('sendback');
    await sendBackToDraft(activeFrame.id, activeDayId);
    await loadAll();
    flash();
    setBusy(null);
  };

  // ---------- STEP 4: APPROVE (Stage 2 approval gate) ----------
  const finalApprove = async () => {
    if (!activeFrame || !activeDayId) return;
    setBusy('approve');
    await setFinalApproved(activeFrame.id, activeDayId);
    await loadAll();
    flash();
    setBusy(null);
    setStep(4);
  };

  const disapprove = async () => {
    if (!activeFrame || !activeDayId) return;
    setBusy('reject');
    await setRejected(activeFrame.id, activeDayId);
    await loadAll();
    flash();
    setBusy(null);
    // Send the post back to Step 3 for reapproval
    setStep(2);
    showGate('Disapproved — sent back to Step 3 for reapproval.');
  };

  // ---------- STEP 5: SCHEDULE ----------
  const schedule = async (time: string) => {
    if (!activeFrame || !activeDayId) return;
    setBusy('schedule');
    await updateFrame(activeFrame.id, { post_time: time, approval_status: 'Scheduled' });
    await supabase.from('content_plan_days').update({ approval_status: 'Scheduled' }).eq('id', activeDayId);
    await loadAll();
    flash();
    setBusy(null);
  };

  // ---------- Gated navigation ----------
  const goNext = () => {
    if (step > 0 && !activeFrame) return;
    // Gate: Step 3 (index 2) -> Step 4 (index 3) requires review approval
    if (step === 2 && !reviewApproved) {
      showGate('Approve this post in Step 3 before sending it to Step 4.');
      return;
    }
    // Gate: Step 4 (index 3) -> Step 5 (index 4) requires final approval
    if (step === 3 && !finalApproved) {
      showGate('Grant final approval in Step 4 before scheduling.');
      return;
    }
    setStep((s) => (Math.min(4, s + 1) as Step));
  };

  // analytics
  const allFrames = days.flatMap((d) => d.frames);
  const counts = {
    total: allFrames.length,
    draft: allFrames.filter((f) => f.approval_status === 'Draft').length,
    reviewApproved: allFrames.filter((f) => f.approval_status === 'Review Approved').length,
    rejected: allFrames.filter((f) => f.approval_status === 'Rejected').length,
    approved: allFrames.filter((f) => f.approval_status === 'Approved').length,
    scheduled: allFrames.filter((f) => f.approval_status === 'Scheduled').length,
  };

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center">
      <div className="w-full max-w-md bg-white min-h-screen flex flex-col shadow-xl relative">
        {/* Top bar */}
        <header className="bg-[#0D1B2A] px-5 pt-6 pb-4 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
                <Compass className="w-4 h-4 text-[#C9A961]" />
              </div>
              <div>
                <h1 className="font-serif text-white text-base leading-none font-light">Zera Content Studio</h1>
                <p className="text-[9px] tracking-[0.25em] uppercase text-[#C9A961] mt-1">Synced with backend</p>
              </div>
            </div>
            <button
              onClick={() => loadAll()}
              className="w-8 h-8 rounded border border-white/15 flex items-center justify-center text-white/60 active:scale-95"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Step progress */}
          <div className="flex items-center gap-1 mt-4">
            {STEPS.map((s, i) => (
              <div key={s.label} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full flex items-center">
                  <div className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-[#C9A961]' : 'bg-white/15'}`} />
                </div>
                <span
                  className={`text-[8px] tracking-wider uppercase flex items-center gap-0.5 ${
                    i === step ? 'text-[#C9A961]' : 'text-white/40'
                  }`}
                >
                  {/* Lock indicator on Step 4 when not yet review-approved */}
                  {i === 3 && activeFrame && !reviewApproved && <Lock className="w-2 h-2" />}
                  {s.short}
                </span>
              </div>
            ))}
          </div>
        </header>

        {/* Sync toast */}
        {synced && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 z-30 bg-emerald-600 text-white text-xs px-4 py-2 rounded-full shadow-lg flex items-center gap-2 animate-in fade-in">
            <Cloud className="w-3.5 h-3.5" /> Saved to backend
          </div>
        )}
        {/* Gate toast */}
        {gateMsg && (
          <div className="fixed top-36 left-1/2 -translate-x-1/2 z-30 bg-[#0D1B2A] text-white text-xs px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 max-w-[88%] text-center animate-in fade-in">
            <Lock className="w-3.5 h-3.5 text-[#C9A961] flex-shrink-0" /> {gateMsg}
          </div>
        )}

        {/* Body */}
        <main className="flex-1 overflow-y-auto px-5 py-5 pb-28">
          <div className="flex items-center gap-2 mb-4">
            {React.createElement(STEPS[step].icon, { className: 'w-5 h-5 text-[#1A3B5C]' })}
            <h2 className="font-serif text-xl text-[#1A3B5C] font-light">
              Step {step + 1} · {STEPS[step].label}
            </h2>
          </div>

          {/* STEP 1 — CREATE */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <p className="text-xs text-slate-500 leading-relaxed">
                  Start a new post. It is created live in the shared backend immediately.
                </p>
                <input
                  value={newDayLabel}
                  onChange={(e) => setNewDayLabel(e.target.value)}
                  placeholder="Post name (e.g. Monday Opener)"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-3 text-sm outline-none focus:border-[#C9A961]"
                />
                <input
                  value={newTheme}
                  onChange={(e) => setNewTheme(e.target.value)}
                  placeholder="Theme (e.g. Riviera morning)"
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-3 text-sm outline-none focus:border-[#C9A961]"
                />
                <button
                  onClick={handleCreate}
                  disabled={busy === 'create'}
                  className="w-full bg-[#1A3B5C] text-white rounded-lg py-3 text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-60"
                >
                  {busy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create Post
                </button>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.2em] uppercase text-slate-400">
                  <ListChecks className="w-3.5 h-3.5" /> Existing posts ({days.length})
                </div>
                {loading ? (
                  <div className="py-8 text-center text-sm text-slate-400">Loading…</div>
                ) : (
                  <div className="space-y-2">
                    {days.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => selectExisting(d)}
                        className="w-full flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-2.5 text-left active:scale-[0.99]"
                      >
                        <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                          {d.frames[0]?.image ? (
                            <img src={d.frames[0].image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImagePlus className="w-4 h-4 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-[#1A3B5C] font-medium truncate">{d.day_label}</div>
                          <div className="text-[11px] text-slate-400 truncate">{d.theme || 'No theme'}</div>
                        </div>
                        <span
                          className={`text-[9px] tracking-wider uppercase px-2 py-1 rounded-full ${statusColor[d.approval_status]}`}
                        >
                          {d.approval_status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Needs a selected frame for steps 2-5 */}
          {step > 0 && !activeFrame && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              Select or create a post first.
              <button onClick={() => setStep(0)} className="block mt-2 underline">
                Go to Create
              </button>
            </div>
          )}

          {/* STEP 2 — GENERATE */}
          {step === 1 && activeFrame && (
            <div className="space-y-4">
              <div className="aspect-square rounded-xl bg-slate-100 overflow-hidden border border-slate-200">
                {activeFrame.image ? (
                  <img src={activeFrame.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-300 gap-2">
                    <ImagePlus className="w-8 h-8" />
                    <span className="text-xs">No image yet</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={generateImage}
                  disabled={!!busy}
                  className="bg-gradient-to-r from-[#8B6F47] to-[#C9A961] text-white rounded-lg py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 disabled:opacity-60"
                >
                  {busy === 'image' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  AI Image
                </button>
                <label className="bg-white border border-slate-200 text-[#1A3B5C] rounded-lg py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 cursor-pointer">
                  {busy === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload
                  <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                </label>
              </div>

              <button
                onClick={generateCaption}
                disabled={!!busy}
                className="w-full bg-gradient-to-r from-[#1A3B5C] to-[#0D1B2A] text-white rounded-lg py-3 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busy === 'caption' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#C9A961]" />}
                Generate Caption with AI
              </button>

              <div>
                <label className="text-[10px] tracking-wider uppercase text-slate-500">Caption</label>
                <textarea
                  value={activeFrame.caption}
                  onChange={(e) => patchFrame({ caption: e.target.value })}
                  rows={5}
                  placeholder="Write or generate a caption…"
                  className="w-full mt-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C9A961]"
                />
              </div>
            </div>
          )}

          {/* STEP 3 — REVIEW (Stage 1 approval gate) */}
          {step === 2 && activeFrame && activeDay && (
            <div className="space-y-4">
              {/* Reapproval banner if it was disapproved in Step 4 */}
              {rejected && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 flex items-start gap-2">
                  <RotateCcw className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-rose-700 leading-relaxed">
                    This post was <strong>disapproved in Step 4</strong> and sent back for reapproval.
                    Re-approve it here to send it back to Step 4.
                  </p>
                </div>
              )}

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
                  <div className="w-7 h-7 rounded-full bg-[#0D1B2A] flex items-center justify-center">
                    <Compass className="w-3.5 h-3.5 text-[#C9A961]" />
                  </div>
                  <span className="text-sm font-semibold text-[#1A3B5C]">zeratravel</span>
                </div>
                <div className="aspect-square bg-slate-100">
                  {activeFrame.image && <img src={activeFrame.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-3 space-y-2">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{activeFrame.caption || '—'}</p>
                  {activeFrame.hashtags?.length > 0 && (
                    <p className="text-xs text-[#1A3B5C]">{activeFrame.hashtags.join(' ')}</p>
                  )}
                  {activeFrame.cta && <p className="text-xs text-slate-500 italic">{activeFrame.cta}</p>}
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-500 space-y-1">
                <div className="flex justify-between"><span>Post type</span><span className="text-slate-700">{activeFrame.post_type}</span></div>
                <div className="flex justify-between"><span>Theme</span><span className="text-slate-700">{activeDay.theme || '—'}</span></div>
                <div className="flex justify-between items-center">
                  <span>Status</span>
                  <span className={`text-[9px] tracking-wider uppercase px-2 py-0.5 rounded-full ${statusColor[status]}`}>
                    {status}
                  </span>
                </div>
              </div>

              {/* Stage 1 approval controls */}
              {reviewApproved ? (
                <div className="space-y-2">
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 flex items-center gap-2 text-sm text-sky-700">
                    <CheckCircle2 className="w-4 h-4" />
                    Review approved — this post can now advance to Step 4.
                  </div>
                  {!finalApproved && (
                    <button
                      onClick={reviewSendBack}
                      disabled={!!busy}
                      className="w-full border border-slate-200 text-slate-500 rounded-lg py-2.5 text-xs font-medium disabled:opacity-60"
                    >
                      Undo — send back to Draft
                    </button>
                  )}
                </div>
              ) : (
                <button
                  onClick={reviewApprove}
                  disabled={!!busy}
                  className="w-full bg-sky-600 text-white rounded-lg py-3.5 text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99] disabled:opacity-60"
                >
                  {busy === 'review' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  {rejected ? 'Re-approve at Review' : 'Approve at Review'}
                </button>
              )}
              <p className="text-[11px] text-slate-400 leading-relaxed text-center">
                A post cannot move to Step 4 until it is approved here in Step 3.
              </p>
            </div>
          )}

          {/* STEP 4 — APPROVE (Stage 2 approval gate) */}
          {step === 3 && activeFrame && (
            <div className="space-y-4">
              {!canEnterApproveStep(status) ? (
                // LOCKED — needs Step 3 review approval first
                <div className="space-y-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center space-y-3">
                    <div className="w-12 h-12 rounded-full bg-slate-200 mx-auto flex items-center justify-center">
                      <Lock className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="text-sm text-slate-600 font-medium">Locked until reviewed</p>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      This post hasn't been approved in Step 3 yet. Approve it at the Review stage
                      first — only reviewed posts reach this final approval gate.
                    </p>
                  </div>
                  <button
                    onClick={() => setStep(2)}
                    className="w-full bg-sky-600 text-white rounded-lg py-3 text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99]"
                  >
                    <ChevronLeft className="w-4 h-4" /> Go to Step 3 — Review
                  </button>
                </div>
              ) : (
                // ELIGIBLE — final approve or disapprove
                <div className="space-y-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center space-y-2">
                    <div
                      className={`inline-flex text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full ${statusColor[status]}`}
                    >
                      Current: {status}
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Final approval marks this post ready in the shared backend. Disapproving sends it
                      back to Step 3 for reapproval.
                    </p>
                  </div>

                  {finalApproved ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2 text-sm text-emerald-700">
                      <CheckCircle2 className="w-4 h-4" /> Approved — ready to schedule in Step 5.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={disapprove}
                        disabled={!!busy}
                        className="bg-white border border-rose-300 text-rose-600 rounded-lg py-3.5 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-[0.99] disabled:opacity-60"
                      >
                        {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                        Disapprove
                      </button>
                      <button
                        onClick={finalApprove}
                        disabled={!!busy}
                        className="bg-emerald-600 text-white rounded-lg py-3.5 text-sm font-medium flex items-center justify-center gap-1.5 active:scale-[0.99] disabled:opacity-60"
                      >
                        {busy === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Approve
                      </button>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 leading-relaxed text-center">
                    Disapproved posts return to Step 3 and must be reapproved before they can come back here.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 5 — SCHEDULE + ANALYZE */}
          {step === 4 && activeFrame && (
            <div className="space-y-5">
              {!finalApproved ? (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-slate-200 mx-auto flex items-center justify-center">
                    <Lock className="w-5 h-5 text-slate-500" />
                  </div>
                  <p className="text-sm text-slate-600 font-medium">Not approved yet</p>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Only fully approved posts can be scheduled. Complete Step 4 first.
                  </p>
                  <button
                    onClick={() => setStep(3)}
                    className="w-full bg-emerald-600 text-white rounded-lg py-3 text-sm font-medium"
                  >
                    Go to Step 4 — Approve
                  </button>
                </div>
              ) : (
                <div>
                  <label className="text-[10px] tracking-wider uppercase text-slate-500">Pick a post time</label>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {['8:00 AM', '9:00 AM', '12:30 PM', '1:00 PM', '5:30 PM', '6:00 PM'].map((t) => (
                      <button
                        key={t}
                        onClick={() => schedule(t)}
                        disabled={!!busy}
                        className={`rounded-lg py-2.5 text-xs font-medium border transition-colors ${
                          activeFrame.post_time === t
                            ? 'bg-[#C9A961] text-white border-[#C9A961]'
                            : 'bg-white text-[#1A3B5C] border-slate-200'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {status === 'Scheduled' && (
                    <div className="mt-3 flex items-center gap-2 text-emerald-700 text-xs">
                      <CheckCircle2 className="w-4 h-4" /> Scheduled for {activeFrame.post_time} — live in backend
                    </div>
                  )}
                </div>
              )}

              {/* Analytics */}
              <div>
                <div className="flex items-center gap-2 mb-2 text-[10px] tracking-[0.2em] uppercase text-slate-400">
                  <BarChart3 className="w-3.5 h-3.5" /> Pipeline overview
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { k: 'Total posts', v: counts.total, c: 'text-[#1A3B5C]' },
                    { k: 'Drafts', v: counts.draft, c: 'text-slate-600' },
                    { k: 'In review', v: counts.reviewApproved, c: 'text-sky-600' },
                    { k: 'Rejected', v: counts.rejected, c: 'text-rose-600' },
                    { k: 'Approved', v: counts.approved, c: 'text-emerald-600' },
                    { k: 'Scheduled', v: counts.scheduled, c: 'text-[#8B6F47]' },
                  ].map((m) => (
                    <div key={m.k} className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <div className={`text-2xl font-serif ${m.c}`}>{m.v}</div>
                      <div className="text-[10px] tracking-wider uppercase text-slate-400 mt-0.5">{m.k}</div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => {
                  setStep(0);
                  setActiveDayId(null);
                  setActiveFrameId(null);
                }}
                className="w-full border border-slate-200 text-[#1A3B5C] rounded-lg py-3 text-sm font-medium"
              >
                Start another post
              </button>
            </div>
          )}
        </main>

        {/* Bottom nav */}
        <nav className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => (Math.max(0, s - 1) as Step))}
            disabled={step === 0}
            className="flex items-center gap-1 text-sm text-slate-500 disabled:opacity-30 active:scale-95"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={() => navigate('/mtnadmin/content-planner')}
            className="text-[10px] tracking-wider uppercase text-slate-300"
          >
            Backend
          </button>
          <button
            onClick={goNext}
            disabled={
              step === 4 ||
              (step > 0 && !activeFrame) ||
              (step === 2 && !reviewApproved) ||
              (step === 3 && !finalApproved)
            }
            className="flex items-center gap-1 text-sm font-medium text-[#1A3B5C] disabled:opacity-30 active:scale-95"
          >
            {step === 2 && !reviewApproved ? (
              <>
                <Lock className="w-3.5 h-3.5" /> Locked
              </>
            ) : step === 3 && !finalApproved ? (
              <>
                <Lock className="w-3.5 h-3.5" /> Locked
              </>
            ) : (
              <>
                Next <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </nav>
      </div>
    </div>
  );
};

export default MobileApp;
